import { Link, useRouterState } from "@tanstack/react-router";
import { Home, Menu, Search, Settings, X } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { Modal } from "@/components/common";
import { isPathActive } from "@/components/layout/SidebarNavItem";
import { UserDropdown } from "@/components/layout/UserDropdown";
import { navigationConfig } from "@/config/navigation";
import { useFilteredNavItems } from "@/hooks/useSidebar";
import type { NavLink } from "@/types/navigation";

export function StudioLayout({ children }: { children: ReactNode }) {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const main = useFilteredNavItems(navigationConfig.main);
  const links: NavLink[] = main.flatMap((entry) =>
    entry.kind === "link" ? [entry] : entry.children,
  );
  const current = [...links]
    .sort((a, b) => b.href.length - a.href.length)
    .find((link) => isPathActive(pathname, link.href, link.exact));
  const [menu, setMenu] = useState(false);
  const [command, setCommand] = useState(false);
  const [query, setQuery] = useState("");
  const label = (item: NavLink) =>
    item.id === "dashboard"
      ? "Biurko"
      : item.id === "clients"
        ? "Klienci i RODO"
        : item.label;
  useEffect(() => {
    const handle = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setCommand((value) => !value);
      }
    };
    window.addEventListener("keydown", handle);
    return () => window.removeEventListener("keydown", handle);
  }, []);
  const closeNavigation = () => {
    setMenu(false);
    setCommand(false);
    setQuery("");
  };
  return (
    <div className="studio-shell">
      <a className="sr-only focus:not-sr-only" href="#studio-main">
        Przejdź do treści
      </a>
      <header className="studio-header">
        <div className="studio-brand">
          <button
            type="button"
            className="studio-mobile-toggle"
            onClick={() => setMenu((value) => !value)}
            aria-label={menu ? "Zamknij menu" : "Otwórz menu"}
            aria-expanded={menu}
          >
            {menu ? <X size={20} /> : <Menu size={20} />}
          </button>
          <Link to="/" aria-label="Adara — moje biurko">
            <img src="/logos/adara-logo-white.svg" alt="Adara Ubezpieczenia" />
          </Link>
          <span className="studio-brand-divider" />
          <span className="studio-brand-name">Pracownia Agenta</span>
        </div>
        <div className="studio-header-actions">
          <button
            type="button"
            className="studio-command-trigger"
            onClick={() => setCommand(true)}
          >
            <Search size={16} aria-hidden />
            <span>Co chcesz zrobić?</span>
            <kbd>⌘ / Ctrl K</kbd>
          </button>
          <Link
            className="studio-settings-shortcut"
            to="/settings"
            search={{ tab: "personalization" }}
            aria-label="Zmień wygląd panelu"
          >
            <Settings size={18} />
          </Link>
          <div className="studio-account">
            <UserDropdown />
          </div>
        </div>
      </header>
      <div className="studio-body">
        <nav
          className={`studio-rail ${menu ? "studio-rail--open" : ""}`}
          aria-label="Menu główne"
        >
          {links.map((link) => (
            <Link
              key={link.id}
              to={link.href}
              onClick={closeNavigation}
              className={
                isPathActive(pathname, link.href, link.exact)
                  ? "studio-nav-link active"
                  : "studio-nav-link"
              }
              aria-current={
                isPathActive(pathname, link.href, link.exact)
                  ? "page"
                  : undefined
              }
            >
              <span>{link.icon}</span>
              <span>{label(link)}</span>
            </Link>
          ))}
          <Link
            to="/settings"
            search={{ tab: "profile" }}
            onClick={closeNavigation}
            className={`studio-nav-link studio-nav-settings ${pathname === "/settings" ? "active" : ""}`}
          >
            <Settings size={18} aria-hidden />
            <span>Ustawienia</span>
          </Link>
        </nav>
        <div className="studio-workspace">
          <div className="studio-workspace-tabs">
            <Link to="/" className={pathname === "/" ? "active" : ""}>
              <Home size={15} aria-hidden />
              Moje biurko
            </Link>
            {pathname !== "/" && (
              <span className="studio-current-tab">
                <span className="studio-gold-dot" />
                {current?.label ??
                  (pathname === "/settings" ? "Ustawienia" : "Panel Agenta")}
              </span>
            )}
            <span className="studio-workspace-note">Adara · Pracownia</span>
          </div>
          <main
            id="studio-main"
            className="studio-main"
            style={{ scrollbarGutter: "stable" }}
          >
            {children}
          </main>
        </div>
      </div>
      <Modal
        isOpen={command}
        onClose={() => setCommand(false)}
        title="Co chcesz zrobić?"
      >
        <label className="sr-only" htmlFor="studio-command-query">
          Szukaj funkcji
        </label>
        <input
          id="studio-command-query"
          className="studio-command-input"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Szukaj funkcji panelu…"
        />
        <div className="studio-command-list">
          {links
            .filter((link) =>
              link.label
                .toLocaleLowerCase("pl")
                .includes(query.toLocaleLowerCase("pl")),
            )
            .map((link) => (
              <Link to={link.href} key={link.id} onClick={closeNavigation}>
                {link.icon}
                <span>{label(link)}</span>
              </Link>
            ))}
          <Link
            to="/settings"
            search={{ tab: "personalization" }}
            onClick={closeNavigation}
          >
            <Settings size={18} aria-hidden />
            <span>Ustawienia i wybór wyglądu</span>
          </Link>
        </div>
      </Modal>
    </div>
  );
}
